<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { canManageImports, ImportPreviewTable, useImportJob } from '@/features/imports'
import { ActionButton, ErrorState, PageHeader, PageShell, StatusTag } from '@taler/ui'
import DetailNotFound from '@/app/components/DetailNotFound.vue'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()
const route = useRoute()
const router = useRouter()

const canView = computed(() => canManageImports(session.can))
const ledgerId = () => session.activeLedger.value?.id
const jobId = () => (typeof route.params['jobId'] === 'string' ? route.params['jobId'] : undefined)

const job = useImportJob({ ledgerId, jobId, enabled: () => canView.value })

const includedEntries = computed(() => job.previewEntries.value.filter((entry) => !entry.excluded))
const canCommit = computed(
  () =>
    job.job.value?.status === 'OPEN' &&
    includedEntries.value.length > 0 &&
    includedEntries.value.every((entry) => entry.counterAccountId !== undefined) &&
    !job.isCommitting.value,
)

async function onSave({
  entryId,
  input,
}: {
  entryId: string
  input: Parameters<typeof job.saveEntry>[1]
}): Promise<void> {
  await job.saveEntry(entryId, input).catch(() => undefined)
}

function onViewDuplicate(entryId: string): void {
  void router.push({ name: 'transactions', query: { entry: entryId } })
}

async function onCommit(): Promise<void> {
  await job.commit().catch(() => undefined)
}

async function onAbandon(): Promise<void> {
  await job.abandon().catch(() => undefined)
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('imports.title')">
      <template #actions>
        <RouterLink :to="{ name: 'imports' }" class="text-[color:var(--color-text-muted)] underline">
          {{ t('imports.backToJobs') }}
        </RouterLink>
      </template>
    </PageHeader>

    <p v-if="!canView" class="text-[color:var(--color-text-muted)]">{{ t('imports.noAccess') }}</p>

    <p v-else-if="job.isLoading.value" role="status">{{ t('imports.detail.loading') }}</p>

    <DetailNotFound v-else-if="job.notFound.value" :to="{ name: 'imports' }" :list="t('imports.title')" />

    <ErrorState
      v-else-if="job.error.value"
      :title="t('imports.detail.errorTitle')"
      :body="t('imports.detail.errorBody')"
      :retry-label="t('imports.list.retry')"
      :retrying="job.retrying.value"
      @retry="job.retry()"
    />

    <template v-else-if="job.job.value !== undefined">
      <div class="flex flex-wrap items-center gap-[var(--space-block)]">
        <p class="text-[color:var(--color-text)]">{{ job.job.value.filename }}</p>
        <StatusTag v-if="job.job.value.status === 'COMMITTED'" tone="ok">
          {{ t('imports.status.COMMITTED') }}
        </StatusTag>
        <StatusTag v-else-if="job.job.value.status === 'ABANDONED'" tone="neutral">
          {{ t('imports.status.ABANDONED') }}
        </StatusTag>
        <StatusTag v-else tone="neutral">{{ t('imports.status.OPEN') }}</StatusTag>

        <button
          v-if="job.job.value.status === 'OPEN'"
          type="button"
          :disabled="job.isAbandoning.value"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)] disabled:opacity-60"
          @click="onAbandon"
        >
          {{ t('imports.detail.abandon') }}
        </button>
      </div>

      <StatusTag v-if="job.job.value.status === 'COMMITTED'" tone="ok">
        {{ t('imports.detail.committedNotice') }}
      </StatusTag>

      <ImportPreviewTable
        :raw-rows="job.rawRows.value"
        :preview-entries="job.previewEntries.value"
        :job-status="job.job.value.status"
        @save="onSave"
        @view-duplicate="onViewDuplicate"
      />

      <StatusTag v-if="job.saveErrorDetail.value !== undefined" tone="danger" role="alert">
        {{ job.saveErrorDetail.value }}
      </StatusTag>

      <StatusTag v-if="job.commitErrorDetail.value !== undefined" tone="danger" role="alert">
        {{ job.commitErrorDetail.value }}
      </StatusTag>

      <div v-if="job.job.value.status === 'OPEN'" class="flex items-center gap-[var(--space-group)]">
        <ActionButton
          size="lg"
          type="button"
          :disabled="!canCommit"
          class="text-[length:var(--text-step-3)] font-medium"
          @click="onCommit"
        >
          {{ job.isCommitting.value ? t('imports.detail.committing') : t('imports.detail.commit') }}
        </ActionButton>
      </div>
    </template>
  </PageShell>
</template>
